<template>
  <div class="row filter-row align-items-end mb-3">
    <div class="col-sm-6 col-md-6" v-if="!hideDepartmentEmployee">
      <div class="input-block mt-1 mb-0">
        <div class="filter-label-row">
          <label class="filter-label">Department</label>
          <i
            v-if="filters.department.length"
            @click="clearFilterField('department')"
            class="fa-solid fa-x filter-clear-icon"
          ></i>
        </div>
        <multiselect
          :class="{ 'has-value': filters.department.length }"
          v-model="filters.department"
          :options="departments"
          label="label"
          track-by="value"
          :multiple="true"
          :searchable="true"
          :close-on-select="false"
          :loading="loadingDepartments"
          placeholder="Select Department"
          @open="fetchDepartments"
        />
      </div>
    </div>
    <div class="col-sm-6 col-md-6" v-if="!hideDepartmentEmployee">
      <div class="input-block mt-1 mb-0">
        <div class="filter-label-row">
          <label class="filter-label">Employee</label>
          <i
            v-if="filters.employee.length"
            @click="clearFilterField('employee')"
            class="fa-solid fa-x filter-clear-icon"
          ></i>
        </div>
        <multiselect
          :class="{ 'has-value': filters.employee.length }"
          v-model="filters.employee"
          :options="employees"
          label="label"
          track-by="value"
          :multiple="true"
          :searchable="true"
          :close-on-select="false"
          :loading="loadingEmployees"
          placeholder="Select Employee"
          @open="fetchEmployees"
        />
      </div>
    </div>
    <div v-if="!hideStatus" :class="hideDepartmentEmployee ? 'col-sm-6 col-md-3' : 'col-sm-6 col-md-2'">
      <div class="input-block mt-1 mb-0">
        <div class="filter-label-row">
          <label class="filter-label">Status</label>
          <i
            v-if="filters.status"
            @click="clearFilterField('status')"
            class="fa-solid fa-x filter-clear-icon"
          ></i>
        </div>
        <vue-select
          :style="`${filters.status && 'border-bottom: var(--primary-color) 2px solid; border-radius: 6px;'}`"
          :options="statusOptions"
          placeholder="Select Status"
          id="gate_pass_status"
          v-model="filters.status"
        />
      </div>
    </div>
    <div :class="hideDepartmentEmployee ? 'col-sm-6 col-md-3' : 'col-sm-6 col-md-2'">
      <div class="input-block mt-1 mb-0">
        <div class="filter-label-row">
          <label class="filter-label">From</label>
          <i
            v-if="filters.from"
            @click="clearFilterField('from')"
            class="fa-solid fa-x filter-clear-icon"
          ></i>
        </div>
        <input
          type="date"
          class="form-control filter-input"
          :class="{ 'has-value': filters.from }"
          v-model="filters.from"
        />
      </div>
    </div>
    <div :class="hideDepartmentEmployee ? 'col-sm-6 col-md-3' : 'col-sm-6 col-md-2'">
      <div class="input-block mt-1 mb-0">
        <div class="filter-label-row">
          <label class="filter-label">To</label>
          <i
            v-if="filters.to"
            @click="clearFilterField('to')"
            class="fa-solid fa-x filter-clear-icon"
          ></i>
        </div>
        <input
          type="date"
          class="form-control filter-input"
          :class="{ 'has-value': filters.to }"
          v-model="filters.to"
        />
      </div>
    </div>
    <div class="col-auto">
      <button class="btn btn-reset m-r-5" @click="resetFilters">
        <i class="fa-solid fa-rotate-left me-1"></i> Reset
      </button>
      <button class="btn btn-filter m-r-5" @click="applyFilters">
        <i class="fa-solid fa-filter me-1"></i> Search
      </button>
    </div>
  </div>

</template>

<script>
import Multiselect from "vue-multiselect";
import EmployeeService from "@/services/EmployeeService";
import DepartmentService from "@/services/DepartmentService";
import { showApiError } from "@/utils";

export default {
  name: "GatePassFilters",
  components: {
    Multiselect,
  },
  props: {
    hideDepartmentEmployee: {
      type: Boolean,
      default: false,
    },
    hideStatus: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["apply-filters"],
  data() {
    return {
      filters: {
        employee: [],
        department: [],
        status: "",
        from: "",
        to: "",
      },
      statusOptions: [
        { id: "pending", text: "Pending" },
        { id: "approved_by_manager", text: "Approved by Manager" },
        { id: "approved_by_hr", text: "Approved by HR" },
        { id: "rejected_by_manager", text: "Rejected by Manager" },
        { id: "rejected_by_hr", text: "Rejected by HR" },
      ],
      employees: [],
      departments: [],
      loadingEmployees: false,
      loadingDepartments: false,
      employeesFetched: false,
      departmentsFetched: false,
    };
  },
  methods: {
    buildFilterParams() {
      const employeeIds = (this.filters.employee || []).map((el) => el.value);
      const departmentIds = (this.filters.department || []).map(
        (el) => el.value,
      );
      return {
        employee_ids: employeeIds,
        department_ids: departmentIds,
        status: this.filters.status || "",
        from: this.filters.from || "",
        to: this.filters.to || "",
      };
    },
    applyFilters() {
      this.$emit("apply-filters", this.buildFilterParams());
    },
    resetFilters() {
      this.filters = {
        employee: [],
        department: [],
        status: "",
        from: "",
        to: "",
      };
      this.applyFilters();
    },
    clearFilterField(key) {
      this.filters[key] = Array.isArray(this.filters[key]) ? [] : "";
      this.applyFilters();
    },
    async fetchEmployees() {
      if (this.employeesFetched || this.loadingEmployees) return;
      this.loadingEmployees = true;
      try {
        const response = await EmployeeService.getAllEmployees();
        this.employees = (response.data || []).map((el) => ({
          value: el.id,
          label: el.employee_name,
        }));
        this.employeesFetched = true;
      } catch (error) {
        showApiError(error, "Failed to fetch employees");
      } finally {
        this.loadingEmployees = false;
      }
    },
    async fetchDepartments() {
      if (this.departmentsFetched || this.loadingDepartments) return;
      this.loadingDepartments = true;
      try {
        const response = await DepartmentService.getDepartments();
        this.departments = (response.data || []).map((el) => ({
          value: el.id,
          label: el.name,
        }));
        this.departmentsFetched = true;
      } catch (error) {
        showApiError(error, "Failed to fetch departments");
      } finally {
        this.loadingDepartments = false;
      }
    },
  },
};
</script>

<style scoped>
.filter-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.filter-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #878787;
  margin: 0;
  padding: 0;
}

.filter-clear-icon {
  font-size: 10px;
  color: #8c9bb5;
  cursor: pointer;
  transition: color 0.2s ease;
}

.filter-clear-icon:hover {
  color: var(--primary-color);
}

:deep(.filter-input) {
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  font-size: 12.5px;
  padding: 6px 12px;
  transition: all 0.2s ease;
}

:deep(.filter-input:focus) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color) 10%, transparent);
}

:deep(.select2-container--default .select2-selection--single) {
  height: 36px !important;
  border-radius: 6px !important;
  border: 1px solid #dcdfe6 !important;
  transition: all 0.2s ease;
}

:deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
  font-size: 12.5px !important;
  line-height: 34px !important;
  padding-left: 12px !important;
  padding-right: 30px !important;
}

:deep(.select2-container--default .select2-selection--single .select2-selection__arrow) {
  height: 34px !important;
}

:deep(.select2-container--open .select2-selection--single) {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color) 10%, transparent);
}

.filter-input.has-value {
  border-bottom: var(--primary-color) 2px solid;
  border-radius: 6px;
}

:deep(.multiselect__tags) {
  min-height: 36px;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  font-size: 12.5px;
}

:deep(.multiselect__select) {
  height: 36px;
}

:deep(.multiselect--active .multiselect__tags) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color) 10%, transparent);
}

:deep(.multiselect.has-value .multiselect__tags) {
  border-bottom: var(--primary-color) 2px solid;
  border-radius: 6px;
}

.btn-filter {
  height: 36px;
  padding: 0 14px;
  font-size: 12.5px;
  font-weight: 700;
  border-radius: 6px;
  background-color: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: #ffffff;
  transition: all 0.2s ease;
}

.btn-filter:hover {
  background-color: color-mix(in srgb, var(--primary-color) 90%, #000);
  border-color: color-mix(in srgb, var(--primary-color) 90%, #000);
  color: #ffffff;
}

.btn-reset {
  height: 36px;
  padding: 0 14px;
  font-size: 12.5px;
  font-weight: 700;
  border-radius: 6px;
  background-color: #ffffff;
  border: 1px solid #dcdfe6;
  color: #606266;
  transition: all 0.2s ease;
}

.btn-reset:hover {
  background-color: #f5f7fa;
  border-color: #c0c4cc;
  color: #303133;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
